import type { ReportKpi, SteeringRow, Week } from 'chery-contracts';
import { BLANK_DISPLAY } from 'chery-kpi-engine';
import { Table, TableCell, TableHead, TableHeaderCell, TableRow } from '../../components/ui/Table';
import { cn } from '../../lib/cn';
import { PILLAR_CLASSES, PILLAR_META } from '../../lib/pillars';
import { useI18nTranslation } from '../../lib/use-i18n-translation';
import { movementTexts, rangeTexts, reportValueText, targetTexts, weekShortLabel } from './reports-display';

export interface SummaryTableProps {
  kpis: readonly ReportKpi[];
  rows: readonly SteeringRow[];
  /** The range's weeks and the week before it, to label the latest and previous values. */
  weekById: ReadonlyMap<string, Pick<Week, 'id' | 'weekDate'>>;
}

const NUMBER_CELL = 'whitespace-nowrap text-end font-mono text-xs';

/**
 * Per KPI: the latest week with a value and the week before it, their neutral movement, then the range's Total /
 * Average / Latest and the target covering the To week with its completion — every figure as the server resolved
 * it, so it matches the Tracker to the digit.
 */
export const SummaryTable = ({ kpis, rows, weekById }: SummaryTableProps) => {
  const { t } = useI18nTranslation();
  const rowOf = new Map(rows.map((row) => [row.kpiId, row]));
  const weekLabel = (weekId: string | undefined): string => {
    const week = weekId === undefined ? undefined : weekById.get(weekId);

    return week ? weekShortLabel(week) : '';
  };

  return (
    <Table data-summary-table>
      <TableHead>
        <tr>
          <TableHeaderCell>{t('reports.columns.kpi')}</TableHeaderCell>
          <TableHeaderCell className="text-end">{t('reports.columns.latestWeek')}</TableHeaderCell>
          <TableHeaderCell className="text-end">{t('reports.columns.previousWeek')}</TableHeaderCell>
          <TableHeaderCell className="text-end">{t('reports.columns.movement')}</TableHeaderCell>
          <TableHeaderCell className="border-s border-border text-end">{t('reports.columns.total')}</TableHeaderCell>
          <TableHeaderCell className="text-end">{t('reports.columns.average')}</TableHeaderCell>
          <TableHeaderCell className="text-end">{t('reports.columns.latest')}</TableHeaderCell>
          <TableHeaderCell className="text-end">{t('reports.columns.target')}</TableHeaderCell>
          <TableHeaderCell className="text-end">{t('reports.columns.completion')}</TableHeaderCell>
        </tr>
      </TableHead>
      <tbody>
        {kpis.map((kpi) => {
          const row = rowOf.get(kpi.id);

          if (!row) {
            return null;
          }

          const movement = movementTexts(kpi, row);
          const range = rangeTexts(kpi, row);
          const target = targetTexts(kpi, row);

          return (
            <TableRow
              key={kpi.id}
              data-kpi-id={kpi.id}
              className="print-avoid-break"
            >
              <TableCell
                className="min-w-[14rem] py-2"
                style={{ paddingInlineStart: `${1 + kpi.depth}rem` }}
                title={kpi.restricted ? t('reports.restricted') : undefined}
              >
                <span className="flex items-center gap-2">
                  <span
                    aria-hidden
                    className={cn('size-2 shrink-0 rounded-full', PILLAR_CLASSES[kpi.pillar].dot)}
                  />
                  {kpi.depth > 0 ? <span className="text-muted-foreground">↳</span> : null}
                  <span className="font-medium">{kpi.name}</span>
                  {kpi.source === 'CALCULATED' ? <span className="text-xs italic text-muted-foreground">ƒ</span> : null}
                  {!kpi.active ? <span className="text-xs text-muted-foreground">({t('reports.inactive')})</span> : null}
                </span>
                <span className="ms-4 text-xs text-muted-foreground">{PILLAR_META[kpi.pillar].shortLabel}</span>
              </TableCell>
              <TableCell
                className={NUMBER_CELL}
                data-col="latestWeek"
              >
                {reportValueText(kpi, row.latest?.value)}
                <span className="block text-[10px] text-muted-foreground">{weekLabel(row.latest?.weekId)}</span>
              </TableCell>
              <TableCell
                className={cn(NUMBER_CELL, 'text-muted-foreground')}
                data-col="previousWeek"
              >
                {reportValueText(kpi, row.previous?.value)}
                <span className="block text-[10px]">{weekLabel(row.previous?.weekId)}</span>
              </TableCell>
              <TableCell
                className={cn(NUMBER_CELL, 'text-muted-foreground')}
                data-col="movement"
              >
                {movement ? (
                  <>
                    <span className="me-1">{movement.glyph}</span>
                    {movement.change}
                    {movement.pct ? <span className="block text-[10px]">{movement.pct}</span> : null}
                  </>
                ) : (
                  BLANK_DISPLAY
                )}
              </TableCell>
              <TableCell
                className={cn(NUMBER_CELL, 'border-s border-border font-semibold')}
                data-col="total"
              >
                {range.total}
              </TableCell>
              <TableCell
                className={NUMBER_CELL}
                data-col="average"
              >
                {range.average}
              </TableCell>
              <TableCell
                className={NUMBER_CELL}
                data-col="latest"
              >
                {range.latest}
              </TableCell>
              <TableCell
                className={NUMBER_CELL}
                data-col="target"
                title={target?.title}
              >
                {target ? (
                  <>
                    {target.value}
                    <span className="block text-[10px] text-muted-foreground">{target.direction}</span>
                  </>
                ) : (
                  <span className="text-muted-foreground">{BLANK_DISPLAY}</span>
                )}
              </TableCell>
              <TableCell
                className={NUMBER_CELL}
                data-col="completion"
              >
                {target ? target.completion : <span className="text-muted-foreground">{BLANK_DISPLAY}</span>}
              </TableCell>
            </TableRow>
          );
        })}
      </tbody>
    </Table>
  );
};
